import { combineReducers, configureStore } from "@reduxjs/toolkit";
import { createSelectorHook, EqualityFn, useDispatch } from "react-redux";
import {
  FLUSH,
  PAUSE,
  PERSIST,
  persistReducer,
  persistStore,
  PURGE,
  REGISTER,
  REHYDRATE,
} from "redux-persist";
import { PersistedState } from "redux-persist/es/types";
import storage from "redux-persist/lib/storage";

import { authSlice } from "./Auth";
import { systemSlice } from "./System";

const PersistVersion = 1;

// @see https://github.com/reduxjs/redux-toolkit/issues/121#issuecomment-480621931
// Create a Persist-Config
const persistConfig = {
  key: "FECSRBoilerplate",
  storage,
  whitelist: [],
  blacklist: ["system"],
  version: PersistVersion,
  migrate: (state: PersistedState) => {
    // eslint-disable-next-line no-underscore-dangle
    if (PersistVersion !== state?._persist.version) {
      return Promise.resolve(null as unknown as PersistedState);
    }
    return Promise.resolve(state);
  },
};

const systemPersistConfig = {
  key: "FECSRBoilerplate-System",
  storage,
  whitelist: ["language"],
  blacklist: [],
  version: PersistVersion,
  migrate: (state: PersistedState) => {
    // eslint-disable-next-line no-underscore-dangle
    if (PersistVersion !== state?._persist.version) {
      return Promise.resolve(null as unknown as PersistedState);
    }
    return Promise.resolve(state);
  },
};

const rootReducer = combineReducers({
  auth: authSlice.reducer,
  system: persistReducer(systemPersistConfig, systemSlice.reducer),
});

const persistedReducer = persistReducer(persistConfig, rootReducer);

const store = configureStore({
  devTools: process.env.NODE_ENV === "development",
  reducer: persistedReducer,
  // @see https://redux-toolkit.js.org/usage/usage-guide#use-with-redux-persist
  middleware: (getDefaultMiddleware) =>
    getDefaultMiddleware({
      serializableCheck: {
        ignoredActions: [FLUSH, REHYDRATE, PAUSE, PERSIST, PURGE, REGISTER],
      },
    }),
});

export type RootState = ReturnType<typeof store.getState>;

export type AppDispatch = typeof store.dispatch;

export const useAppSelector = createSelectorHook() as <
  TState = RootState,
  Selected = unknown
>(
  selector: (state: TState) => Selected,
  equalityFn?: EqualityFn<Selected> | undefined
) => Selected;

export const useAppDispatch = () => useDispatch<AppDispatch>();

export const persistor = persistStore(store);

export default store;
